import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { Button, IconButton } from '@mui/material';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Dialog, DialogActions, DialogContent } from '@mui/material';
import { format } from 'date-fns';
import { addOrderShareByEmail, addProjectShareByEmail, getOrderShares, getProjectShares, removeOrderShare, removeProjectShare } from '../api/orders';
import { AccountContext } from '../context/account';
import { FullAccount, ShareDataItem } from '../core/global';
import { userEmailShape } from '../data/form-shapes';
import { getItemByKeyValue } from '../helpers/core';
import styles from "../styles/components/project-item-share.module.scss";
import tableStyles from "../styles/components/table.module.scss";
import { CopyButton } from './copy-button';
import { WarningMessage } from './error-message';
import { CreateForm } from './form-components';
import Loader from './loader';
import { ModalTitle } from './modal-title';

interface ProjectItemShareModalProps {
  onClose: () => void
  itemId: number | string | null
  type: 'item' | 'project'
}

export const ProjectItemShareModal = ({
  onClose,
  itemId,
  type,
}: ProjectItemShareModalProps) => {
  const [addNewUser, setAddNewUser] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const [loading, setLoading] = React.useState(false)
  const [sharesData, setSharesData] = React.useState<ShareDataItem[] | null>(null)
  
  const {
    account,
  } = useContext(AccountContext);

  const refetchShares = useCallback( async () => {
    if (!itemId) {
      return
    }
    setLoading(true)

    const {response} = type === 'item' ? await getOrderShares({order_item_id: itemId}) : await getProjectShares({project_id: itemId})
    if (response) {
      const {shares} = response
      setSharesData(shares)
    } else {
      setSharesData(null)
    }

    setLoading(false)
  }, [itemId])

  useEffect(() => {
    setShowWarning(false)
    refetchShares()
  }, [itemId])

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <ModalTitle onClose={onClose}>{type === 'item' ? 'Data point' : 'Project'} sharing</ModalTitle>
      <DialogContent>
        <p>People with access to this {type === 'item' ? 'data point' : 'project folder'}.</p>
        {/* add this user */}
        {loading ? 
          <div style={{margin: "64px 0"}}>
            <Loader small center />
          </div>
        : 
          <div className={styles.manageSharing}>
            <ManageSharing 
              account={account} 
              data={sharesData} 
              onUpdate={() => refetchShares()} 
              type={type}
            />
            {addNewUser ? 
              <>
                <CreateForm
                  size='small'
                  title={'Share with user'}
                  intro={<>Share with user</>}
                  isModal={false}
                  inline
                  shape={userEmailShape}
                  onChange={(vals, key) => {
                    if (key === 'email') {
                      const {email} = vals
                      if (email && sharesData) {
                        const found = getItemByKeyValue([...sharesData, account], 'email', email )
                        if (found !== -1) {
                          setShowWarning(true)
                        } else {
                          setShowWarning(false)
                        }
                      }
                    }
                  }}
                  successMessage="Data has been shared with a new user"
                  defaultValues={null}
                  onCancel={() => {
                    setAddNewUser(false)
                  }}
                  onSuccess={() => {
                    setAddNewUser(false)
                    refetchShares()
                  }}
                  disableSubmit={showWarning}
                  onSubmit={(vals ) => {
                    return type === 'item' ? addOrderShareByEmail({...vals, order_item_id: itemId}) : addProjectShareByEmail({...vals, project_id: itemId})
                  }}
                  onError={(message?: string | null | undefined) => {
                    console.log('error', message)
                  }}
                />
                {showWarning && <WarningMessage message={[type === 'item' ? 'User has already access to this data location...' : 'User has already access to this project folder...']} />}
              </>
              :
              <>
                <Button onClick={() => {
                  setAddNewUser(true)
                  setShowWarning(false)
                }}>
                  <AddIcon />Share with user by email
                </Button>
              </>
            }
          </div>
        }
      </DialogContent>
      <>
        <DialogActions sx={{justifyContent: 'flex-end', pb: 3, px: 3}}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </>
    </Dialog>

  )
}

interface ManageSharingItemProps {
  data: ShareDataItem
  onUpdate: () => void
  showActions?: boolean
  type: 'item' | 'project'
}

export const ManageSharingItem = ({
  data, 
  onUpdate,
  showActions = true,
  type,
}: ManageSharingItemProps) => {
  const [editedRowId, setEditedRowId] = useState<number | string | null>(null)

  const {id, email, created_at, first_name, last_name} = data

  const useDate = created_at ? format(new Date(created_at), 'dd MMMM yyyy') : '';
  const useTime = created_at ? format(new Date(created_at), 'HH:mm') : '';
  const isEdit = editedRowId !== null && editedRowId === id
  return (
    <tr>
      <td>
        <strong>{first_name} {last_name}</strong><br />
        {email && <>{email}{showActions && <CopyButton content={email} />}<br /></>}
      </td>
      <td className={tableStyles.date}>
        {useDate && useTime ?
          <>{useDate}<span className={styles.time}><AccessTimeIcon />{useTime}</span></>
          : ``
        }
      </td>
      <td className={tableStyles.actions}>
        {!showActions ?
          null
          :
          <>
            {!isEdit ?
              <IconButton 
                size='small'
                onClick={() => {
                  if (id) setEditedRowId(id)
                }
              }>
                <DeleteIcon />
              </IconButton>
              :
              <div>
                <Button 
                  size='small'
                  color='error'
                  startIcon={<DeleteForeverIcon />}
                  onClick={async () => {
                    if (type === 'item') {
                      await removeOrderShare({id})
                    } else {
                      await removeProjectShare({id})
                    }
                    onUpdate()
                  }}>
                  Revoke
                </Button>
                <Button 
                  size='small'
                  color="success"
                  onClick={() => {
                    setEditedRowId(null)
                  }}>
                  Keep
                </Button>
              </div>
            }
          </>
        }
      </td>
    </tr>
  )
}

interface ManageSharingProps {
  data: ShareDataItem[] | null
  onUpdate: () => void
  account: FullAccount
  type: 'item' | 'project'
}


export const ManageSharing = ({
  data, 
  onUpdate,
  account,
  type,
}: ManageSharingProps) => {
  
  const userAccount: ShareDataItem = useMemo(() => {
    const {email, first_name, last_name, uid} = account
    return {
      id: null,
      uid,
      project_id: null,
      order_item_id: null,
      created_at: '',
      email,
      first_name,
      last_name: `${last_name} (you)`,
      job_title: '',
    }
  }, [account])

  return (
    <>
      <table className={tableStyles.table}>
        <thead>
          <tr>
            <th className={tableStyles.fieldName} style={{minWidth: '300px'}}>
              User
            </th>
            <th>Shared on</th>
            <th className={tableStyles.actions}>
              <span>Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
        <ManageSharingItem data={userAccount} onUpdate={onUpdate} showActions={false} type={type} />
        {data && data.length > 0 ?
          data.map((item: ShareDataItem) => {
            if (`${item.email}`.toLowerCase() === `${userAccount.email}`.toLowerCase()) {
              return null
            }
            return (
              <ManageSharingItem key={item.id} data={item} onUpdate={onUpdate} type={type} />
            )
          })
          :
          null
        }
        </tbody>
      </table>
    </>
  )
}

